import { queryClient, reactQueryUtils } from "@lib/react-query";
import { useMutation } from "@tanstack/react-query";
import {
  apiKeysApi,
  type ICreateApiKeyParams,
  type IUpdateApiKeyParams,
} from "./api-keys.api";
import { apiKeysKeys } from "./api-keys.keys";
import type { TApiKey, TApiKeyCreateResponse } from "./types";

export const useCreateApiKeyMutation = () => {
  return useMutation({
    mutationFn: (data: ICreateApiKeyParams) => apiKeysApi.createApiKey(data),
    onSuccess: (data: TApiKeyCreateResponse) => {
      // Extract the API key data without the token for caching
      const { token, ...apiKeyData } = data;

      // Optimistically update the API keys list
      queryClient.setQueryData<TApiKey[]>(
        apiKeysKeys.apiKeys,
        (old: TApiKey[] | undefined) => {
          if (!old) return [apiKeyData];
          return [apiKeyData, ...old];
        },
      );
      reactQueryUtils.invalidateQueries([...apiKeysKeys.apiKeys]);
    },
  });
};

export const useUpdateApiKeyMutation = () => {
  return useMutation({
    mutationFn: (data: IUpdateApiKeyParams) => apiKeysApi.updateApiKey(data),
    onSuccess: (data: TApiKey, variables: IUpdateApiKeyParams) => {
      // Optimistically update the API keys list
      queryClient.setQueryData<TApiKey[]>(
        apiKeysKeys.apiKeys,
        (old: TApiKey[] | undefined) => {
          if (!old) return [];
          const idx = old.findIndex((el: TApiKey) => el._id === variables.id);
          if (idx >= 0) {
            const updated = [...old];
            updated[idx] = { ...updated[idx], ...data };
            return updated;
          }
          return old;
        },
      );
      // Invalidate specific API key query
      reactQueryUtils.invalidateQueries([...apiKeysKeys.apiKeys]);
    },
  });
};

export const useUpdateApiKeyStatusMutation = () => {
  return useMutation({
    mutationFn: (data: IUpdateApiKeyParams) => apiKeysApi.updateApiKey(data),
    onSuccess: (_: TApiKey, variables: IUpdateApiKeyParams) => {
      // Optimistically update the API keys list
      queryClient.setQueryData<TApiKey[]>(
        apiKeysKeys.apiKeys,
        (old: TApiKey[] | undefined) => {
          if (!old) return [];
          const idx = old.findIndex((el: TApiKey) => el._id === variables.id);
          if (idx >= 0) {
            const updated = [...old];
            updated[idx] = {
              ...updated[idx],
              status: variables.status ?? "active",
            };
            return updated;
          }
          return old;
        },
      );
      // Invalidate specific API key query
      reactQueryUtils.invalidateQueries([...apiKeysKeys.apiKeys]);
    },
  });
};

export const useDeleteApiKeyMutation = () => {
  return useMutation({
    mutationFn: (id: string) => apiKeysApi.deleteApiKey(id),
    onSuccess: (_: TApiKey, variables: string) => {
      // Optimistically update the API keys list
      queryClient.setQueryData<TApiKey[]>(
        apiKeysKeys.apiKeys,
        (old: TApiKey[] | undefined) => {
          if (!old) return [];
          return old.filter((apiKey: TApiKey) => apiKey._id !== variables);
        },
      );
      reactQueryUtils.invalidateQueries([...apiKeysKeys.apiKeys]);
    },
  });
};

export const useRegenerateApiKeyMutation = () => {
  return useMutation({
    mutationFn: (id: string) => apiKeysApi.regenerateApiKey(id),
    onSuccess: () => {
      reactQueryUtils.invalidateQueries([...apiKeysKeys.apiKeys]);
    },
  });
};

export const useRevokeApiKeyMutation = () => {
  return useMutation({
    mutationFn: (id: string) => apiKeysApi.revokeApiKey(id),
    onSuccess: (data: TApiKey, variables: string) => {
      // Optimistically update the API keys list
      queryClient.setQueryData<TApiKey[]>(
        apiKeysKeys.apiKeys,
        (old: TApiKey[] | undefined) => {
          if (!old) return [];
          const idx = old.findIndex((el: TApiKey) => el._id === variables);
          if (idx >= 0) {
            const updated = [...old];
            updated[idx] = { ...updated[idx], ...data };
            return updated;
          }
          return old;
        },
      );
      reactQueryUtils.invalidateQueries([...apiKeysKeys.apiKeys]);
    },
  });
};
